import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Edit, Information } from '@carbon/react/icons';
import {
  Tile,
  Link,
  Grid,
  Column,
  Tooltip,
  CopyButton,
  Button,
} from '@carbon/react';

import { ServiceEntryPoint } from '../../../models/master';
import images from '../../../images/images';
import { getIpCountInRange } from '../../../lib/utils';

import { VerticalEmptyState } from '../../../components/EmptyState/EmptyState';

import './ExternalClientAccessCard.scss';

const ExternalClientAccessCard = (props: {
  access: string;
  ipAddressPool: string;
  serviceEntryPoints: ServiceEntryPoint[];
  openEditTearsheet: () => void;
}) => {
  const { access, serviceEntryPoints, ipAddressPool, openEditTearsheet } =
    props;
  const { t } = useTranslation('gatewayDetails');
  const navigate = useNavigate();

  const getRemainingIpAddresses = () => {
    const totalIps = ipAddressPool
      .split(', ')
      .reduce((acc: number, curr: string) => acc + getIpCountInRange(curr), 0);
    return (
      <>
        <span className='remaining'>
          {totalIps - serviceEntryPoints.length}
        </span>
        <span className='total'>
          {t('externalClientAccess.ipAddressTotal', {
            count: totalIps,
          })}
        </span>
      </>
    );
  };

  return (
    <Tile className='external-client-access-tile'>
      <div className='eac-header'>
        <div className='text'>{t('externalClientAccess.header')}</div>
        <div className='learn-more-link'>
          <Link
            href={
              navigator.language === 'fr' ||
              navigator.language === 'fr-FR' ||
              navigator.language === 'fr-BE' ||
              navigator.language === 'fr-CA' ||
              navigator.language === 'fr-CH'
                ? 'https://ibm.biz/mesh-external-client-access-fr'
                : 'https://ibm.biz/mesh-external-client-access'
            }
            target='_blank'
            rel='noopener noreferrer'
          >
            {t('learnMore')}
          </Link>
        </div>

        {access !== 'disabled' && serviceEntryPoints.length > 0 && (
          <Button
            kind='ghost'
            iconDescription={t('edit')}
            tooltipPosition='bottom'
            hasIconOnly
            renderIcon={() => <Edit size={16} />}
            className='edit-button'
            onClick={() => openEditTearsheet()}
          ></Button>
        )}
      </div>
      {access === 'disabled' ? (
        <VerticalEmptyState
          link={true}
          additionalClassName='eac-empty-state'
          icon={images.notAuthorizedIcon()}
          header={t('externalClientAccess.emptyState.noAccessTitle')}
          description={t('externalClientAccess.emptyState.noAccessDescription')}
          buttonText={t('externalClientAccess.emptyState.noAccessButtonText')}
          click={openEditTearsheet}
        />
      ) : serviceEntryPoints.length === 0 ? (
        <VerticalEmptyState
          link={true}
          additionalClassName='eac-empty-state'
          icon={images.noServiceEndpointSmallIcon()}
          header={t('externalClientAccess.emptyState.noEntryPointsTitle')}
          description={t(
            'externalClientAccess.emptyState.noEntryPointsDescription'
          )}
          buttonText={t(
            'externalClientAccess.emptyState.noEntryPointsButtonText'
          )}
          navigateButton
          click={() => navigate('/connectionAccessPolicies')}
        />
      ) : (
        <>
          <Grid className='summary'>
            <Column lg={6} className='col'>
              <div className='label'>
                {t('externalClientAccess.entryPoints')}
              </div>
              <div className='value'>{serviceEntryPoints.length}</div>
            </Column>
            <Column lg={6} className='col'>
              <div className='label'>
                {t('externalClientAccess.remainingIpAddresses')}
                <Tooltip
                  label={t('externalClientAccess.ipAddressTooltip')}
                  align='bottom'
                >
                  <Information size={16} />
                </Tooltip>
              </div>
              <div className='value'>{getRemainingIpAddresses()}</div>
            </Column>
          </Grid>

          <div className='service-entrypoint-list'>
            {serviceEntryPoints.map((sep: ServiceEntryPoint) => (
              <div className='service-entrypoint-row'>
                <div className='info'>
                  <div className='label'>{sep.service_name}</div>
                  <div className='ips'>{sep.entry_points}</div>
                </div>
                <CopyButton
                  className='copy-button'
                  iconDescription={t('copy')}
                  onClick={() =>
                    navigator.clipboard.writeText(sep.entry_points)
                  }
                />
              </div>
            ))}
          </div>
          <div className='fading'></div>
        </>
      )}
    </Tile>
  );
};

export default ExternalClientAccessCard;
